import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resetPassword, useAppDispatch } from '../redux';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { validateForm, getPasswordStrength } from '../utils/validation';
import { showToast } from '../utils/toast';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const email = localStorage.getItem("resetEmail");
    const code = localStorage.getItem("resetCode");
    if (!email || !code) navigate("/request-password-reset");
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate form
    const validation = validateForm(formData, 'resetPassword');
    if (!validation.isValid) {
      setErrors(validation.errors);
      setTouched({
        password: true,
        confirmPassword: true
      });
      setLoading(false);
      return;
    }
    
    try {
      const email = localStorage.getItem('resetEmail');
      const code = localStorage.getItem("resetCode");
      await dispatch(
        resetPassword({ email, code, newPassword: formData.password })
      ).unwrap();
      localStorage.removeItem("resetEmail");
      localStorage.removeItem("resetCode");
      localStorage.removeItem("resetUserId");
      // Show success message and redirect to login
      showToast.success('Password reset successful! Please login with your new password.');
      navigate('/login', { 
        state: { message: 'Password reset successful. Please login with your new password.' }
      });
    } catch (error) {
      setError(
        typeof error === "string"
          ? error
          : error?.message || "Failed to reset password"
      );
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <>
      <Header />
      <div className="container-fluid auth-container" style={{ minHeight: 'calc(100vh - 200px)', paddingTop: '10rem', paddingBottom: '8rem' }}>
        <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="card p-4 shadow-lg" style={{ width: '100%', maxWidth: '450px' }}>
            <h3 className="text-center mb-4">Reset Password</h3>
            
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="password" className="form-label">New Password *</label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className={`form-control ${touched.password && errors.password ? 'is-invalid' : ''}`}
                    placeholder="Enter new password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                    minLength="8"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <div className="invalid-feedback">{errors.password}</div>
                )}
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-1">
                      <small className="text-muted me-2">Password strength:</small>
                      <div className="progress flex-grow-1" style={{ height: '4px' }}>
                        <div 
                          className={`progress-bar ${
                            passwordStrength.strength <= 2 ? 'bg-danger' : 
                            passwordStrength.strength <= 3 ? 'bg-warning' : 'bg-success'
                          }`}
                          style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <small className="text-muted">
                        Missing: {passwordStrength.feedback.join(', ')}
                      </small>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">Confirm Password *</label>
                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    className={`form-control ${touched.confirmPassword && errors.confirmPassword ? 'is-invalid' : ''}`}
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading}
                    minLength="8"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {touched.confirmPassword && errors.confirmPassword && (
                  <div className="invalid-feedback">{errors.confirmPassword}</div>
                )}
              </div>

              <button 
                type="submit" 
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Remember your password? <Link to="/login">Login</Link>
                </small>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ResetPassword; 